import { useState } from 'react';
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Calendar as CalendarIcon, Plus, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { AddAvailabilityDialog } from "./AddAvailabilityDialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';

interface AvailabilityCalendarProps {
  hostId: string;
  isOwnProfile?: boolean;
}

export const AvailabilityCalendar = ({ hostId, isOwnProfile = false }: AvailabilityCalendarProps) => {
  const [date, setDate] = useState<Date | undefined>(new Date());
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const { data: availabilities, isLoading } = useQuery({
    queryKey: ['host-availability', hostId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('host_availability')
        .select('*')
        .eq('host_id', hostId);
      
      if (error) throw error;
      return data;
    },
  });

  // Function to check if a date has availability slots
  const hasAvailability = (day: Date) => {
    if (!availabilities) return false;
    const dayStr = format(day, 'yyyy-MM-dd');
    return availabilities.some(slot => {
      const slotDate = format(new Date(slot.start_time), 'yyyy-MM-dd');
      return slotDate === dayStr;
    });
  };

  // Function to get availability count for a specific date
  const getAvailabilityCount = (day: Date) => {
    if (!availabilities) return 0;
    const dayStr = format(day, 'yyyy-MM-dd');
    return availabilities.filter(slot => {
      const slotDate = format(new Date(slot.start_time), 'yyyy-MM-dd');
      return slotDate === dayStr;
    }).length;
  };

  return (
    <Card className="animate-fadeIn">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="text-2xl font-bold">Availability Calendar</CardTitle>
          <CardDescription className="mt-1">
            Select dates to manage your availability
          </CardDescription>
        </div>
        {isOwnProfile && (
          <Button onClick={() => setIsDialogOpen(true)} className="ml-auto hover:scale-105 transition-transform">
            <Plus className="mr-2 h-4 w-4" /> Add Availability
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <Alert className="mb-4">
          <Info className="h-4 w-4" />
          <AlertDescription>
            {isOwnProfile 
              ? "Click on any date to add or manage your availability slots. Dates with existing slots are highlighted."
              : "Browse through the calendar to view available slots. Highlighted dates indicate availability."
            }
          </AlertDescription>
        </Alert>
        <div className="flex justify-center p-4">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            className="rounded-md border"
            modifiersStyles={{
              today: {
                fontWeight: 'bold',
                textDecoration: 'underline',
              },
              selected: {
                backgroundColor: '#8B5CF6',
                color: 'white',
                fontWeight: 'bold',
              },
              hasSlots: {
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                fontWeight: 'bold',
              }
            }}
            modifiers={{
              hasSlots: (date) => hasAvailability(date),
            }}
            components={{
              DayContent: ({ date }) => {
                const count = getAvailabilityCount(date);
                return (
                  <div className="relative w-full h-full flex items-center justify-center">
                    <span>{date.getDate()}</span>
                    {count > 0 && (
                      <Badge 
                        variant="secondary" 
                        className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center text-[10px]"
                      >
                        {count}
                      </Badge>
                    )}
                  </div>
                );
              }
            }}
          />
        </div>
        {isOwnProfile && (
          <AddAvailabilityDialog
            open={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            hostId={hostId}
            selectedDate={date}
          />
        )}
      </CardContent>
    </Card>
  );
};